/**
 * Resolve once `predicate()` is true, or after `timeoutMs`, whichever comes first.
 *
 * Returns true when the predicate was satisfied and false when it timed out. Never rejects, so a caller can
 * `await` it before a step that should still happen even if the condition never settles.
 *
 * Used before starting an interactive voice session: a chat reopened from history restores its last card
 * asynchronously, and a session that starts before that finishes begins with an empty screen and is corrected a
 * few seconds later. Waiting (bounded) lets the session start with the card the user is actually looking at.
 */
export function waitUntil(
  predicate: () => boolean,
  timeoutMs = 3000,
  intervalMs = 50,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let done = false;
    const start = Date.now();

    const check = () => {
      if (done) return;
      let ok = false;
      try {
        ok = predicate();
      } catch {
        ok = false;
      }
      if (ok) {
        done = true;
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        done = true;
        resolve(false);
        return;
      }
      setTimeout(check, intervalMs);
    };

    check();
  });
}
